import React from 'react'
import { node, string } from 'prop-types'
import { Link as GatsbyLink } from 'gatsby'
import { Link as MuiLink } from '@material-ui/core'
import { withStyles } from '@material-ui/styles'
import { getAdditionalLinkAttributes } from '@jelly/utils'

const getStyles = (variation, theme) => {
	let color
	let hover
	switch (variation) {
		case 'primary':
			color = theme.palette.primary.main
			hover = theme.palette.secondary.main
			break
		case 'tertiary':
			color = theme.palette.tertiary.main
			hover = theme.palette.primary.main
			break
		case 'contentLink':
			color = theme.palette.link.content.main
			hover = theme.palette.secondary.main
			break
		case 'authorLink':
			color = theme.palette.text.primary
			hover = theme.palette.link.author.hover
			break
		case 'breadcrumb':
			color = theme.palette.grey[600]
			hover = theme.palette.link.breadcrumb.main
			break
		case 'light':
			color = theme.palette.common.white
			hover = theme.palette.secondary.main
			break
		case 'secondary':
		default:
			color = theme.palette.secondary.main
			hover = theme.palette.tertiary.main
			break
	}
	return { color, hover }
}

const LinkWrapper = withStyles(
	(theme) => {
		return {
			root: {
				color: ({ variation }) => getStyles(variation, theme).color,
				'&:hover': {
					color: ({ variation }) => getStyles(variation, theme).hover,
				},
			},
		}
	},
	{ name: 'linkWrapper' }
)(MuiLink)

Link.propTypes = {
	children: node.isRequired,
	href: string,
	variant: string,
}

/**
 * Link - common component

 * @param {Node} props.children :: what the link should wrap
 * @param {String} props.string :: url path the link is directed to
 * @param {String} props.variant :: determines what type of theme the text or button link should display
 * any other prop you want
 *
 * Additionally, it features:
 * - a spread operator to handle relationship and target attributes
 *
 **/
export function Link({ children, className, href, variant = 'primary', ...restOfProps }) {
	const IS_GATSBY_SITE = process.env.GATSBY_ENABLE_GATSBY_LINKS === 'true'

	// regex to check for exactly one leading slash
	const isInternal = /^\/(?!\/)/.test(href)
	const isGatsbyLink = isInternal && IS_GATSBY_SITE

	return (
		<>
			<LinkWrapper
				className={className}
				variation={variant}
				color="inherit" // leave this for typography styles
				underline="none"
				{...(isGatsbyLink ? { to: href } : { href })}
				{...getAdditionalLinkAttributes(href)}
				{...(isGatsbyLink && { component: GatsbyLink })}
				{...restOfProps}
			>
				{children}
			</LinkWrapper>
		</>
	)
}

export default Link
